import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import MoreFilterCRM from "../ui/shared/MoreFilterCRM";
import type { OpportunityFilters } from "../ui/shared/MoreFilterCRM";
import Search from "../ui/shared/Search";
import type { Pipeline } from "@/hooks/usePipelinesSettings";
import type { DateFilterPeriod } from "@/hooks/useOpportunities";
import { DEFAULT_CRM_DATE_FIELD, getCrmDateFieldOption } from "@/lib/crmDateFilter";
import { CheckCircle2, Loader2, Calendar } from "lucide-react";

const DATE_FILTER_OPTIONS: { value: DateFilterPeriod; label: string }[] = [
  { value: '30d', label: 'Ultimos 30 dias' },
  { value: '60d', label: 'Ultimos 60 dias' },
  { value: '90d', label: 'Ultimos 90 dias' },
  { value: 'all', label: 'Todas' },
];

interface CrmHeaderProps {
  pipelines: Pipeline[];
  selectedPipelineId: string;
  onPipelineChange: (pipelineId: string) => void;
  onSearchChange: (searchTerm: string) => void;
  searchTerm: string;
  currentFilters: OpportunityFilters;
  onFilterChange: (filters: OpportunityFilters) => void;
  onClearFilters: () => void;
  dateFilter: DateFilterPeriod;
  onDateFilterChange: (period: DateFilterPeriod) => void;
  loadingProgress?: {
    loaded: number;
    total: number;
    isLoadingMore: boolean;
  };
}

const triggerClass =
  "w-full h-[42px] bg-white border-border rounded-[12px] text-foreground shadow-sm transition-colors duration-200 focus:border-[#2563EB] hover:bg-[#F8FAFC] dark:bg-neutral-600 dark:border-[#3F3F46] dark:text-neutral-50 dark:shadow-none dark:hover:bg-neutral-500";

const CrmHeader = ({ pipelines, selectedPipelineId, onPipelineChange, onSearchChange, searchTerm, currentFilters, onFilterChange, onClearFilters, dateFilter, onDateFilterChange, loadingProgress }: CrmHeaderProps) => {
  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId);
  const selectedDateOption = DATE_FILTER_OPTIONS.find(o => o.value === dateFilter);
  const dateField = currentFilters.dateField || DEFAULT_CRM_DATE_FIELD;
  const dateFieldLabel = getCrmDateFieldOption(dateField).label;
  const periodLabel = selectedDateOption?.label || 'Periodo';
  const periodDisplay =
    dateField !== DEFAULT_CRM_DATE_FIELD && dateFilter !== 'all'
      ? `${periodLabel} · ${dateFieldLabel}`
      : periodLabel;

  return (
    <div className="mb-[30px] flex flex-col gap-4 border-b border-border pb-4">
      <div className="flex flex-wrap gap-[15px] items-center">
        <Select value={selectedPipelineId} onValueChange={onPipelineChange}>
          <SelectTrigger className={`${triggerClass} max-w-[220px]`}>
            <SelectValue placeholder="Selecione um pipeline">
              {selectedPipeline?.name || "Selecione um pipeline"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {pipelines.filter(p => p.is_active).map((pipeline) => (
                <SelectItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <Select value={dateFilter} onValueChange={(value) => onDateFilterChange(value as DateFilterPeriod)}>
          <SelectTrigger
            className={`${triggerClass} max-w-[280px]`}
            title={`Periodo com base em: ${dateFieldLabel}`}
          >
            <Calendar className="w-4 h-4 mr-2 text-muted-foreground dark:text-neutral-400 shrink-0" />
            <SelectValue>
              {periodDisplay}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {DATE_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <Search
          styles="w-full h-[40px] max-w-[220px]"
          placeholder="Pesquisar..."
          onSearchChange={onSearchChange}
          value={searchTerm}
        />
        <div className="w-fit text-nowrap flex items-center gap-3">
          <MoreFilterCRM
            currentFilters={currentFilters}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
          />
          {loadingProgress && loadingProgress.loaded > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] rounded-[12px] border border-border shadow-sm dark:bg-neutral-800 dark:border-neutral-700 dark:shadow-none">
              {loadingProgress.isLoadingMore ? (
                <Loader2 className="w-4 h-4 text-[#2563EB] animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-[#16A34A]" />
              )}
              <span className="text-sm text-muted-foreground dark:text-neutral-300">
                {loadingProgress.loaded} oportunidade{loadingProgress.loaded !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrmHeader;
